import React from 'react';

import type { EditScannerConfigInputV2 } from '@rtkwlf/rendall';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Grid, Typography } from '@packages/fenrir/components';
import { Stack } from '@packages/fenrir/components/stack';

import { useEnhancedScannerDetails } from '../../../../../../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import type { ScannerDetailsForm } from '../../../../types';

import { ScannerConfigurationDropdown } from './components/scanner-configuration-dropdown';
import { ScannerConfigurationSwitch } from './components/scanner-configuration-switch';
import { useHostIdScanIntensityOptions } from './components/use-host-id-scan-intensity-options';
import { useMaxConcurrentVulScansOptions } from './components/use-max-concurrent-vul-scans-options';
import { useScanPeriodOptions } from './components/use-scan-period-options';

const oneBoxGridSize = { xs: 12, md: 5, lg: 3.5, xl: 2.2 };

export type ScannerConfigurationDropdownConfigs = Pick<
  EditScannerConfigInputV2,
  | 'hostIdentificationScanIntensity'
  | 'hostIdentificationScanPeriod'
  | 'maxConcurrentVulnerabilityScans'
>;

export const ScannerConfiguration: React.FC = () => {
  const { t } = useTranslation('telemetryScannerConfig');
  const { scannerId = '' } = useParams();
  const { data: scannerDetails, isPending } =
    useEnhancedScannerDetails(scannerId);
  const { control } = useFormContext<ScannerDetailsForm>();
  const hostIdentificationScanningIntensityOptions =
    useHostIdScanIntensityOptions();
  const scanPeriodOptions = useScanPeriodOptions();
  const maxConcurrentVulScansOptions = useMaxConcurrentVulScansOptions();

  const hasScanOutsideWindowDisabledCapability =
    scannerDetails?.capabilities.includes('scan_outside_window_disabled');

  return (
    <Stack data-testid="main-scanner-config-column" spacing={2} sx={{ mb: 4 }}>
      {/* Temporary solution to match other tabs using fenrir v1 */}
      <Typography variant="h4" sx={{ fontSize: '1rem', fontWeight: 700 }}>
        {t('scannerConfiguration.title')}
      </Typography>
      <Grid container spacing={4}>
        <ScannerConfigurationSwitch
          control={control}
          controlName="hostIden"
          label={t('scannerConfiguration.hostIdentification')}
          description={t('scannerConfiguration.hostIdentificationDescription')}
          isPending={isPending}
          isDisabled={!scannerDetails?.isConnected}
          gridSize={oneBoxGridSize}
        />
        <ScannerConfigurationSwitch
          control={control}
          controlName="vulnScan"
          label={t('scannerConfiguration.vulnerabilityScanning')}
          description={t(
            'scannerConfiguration.vulnerabilityScanningDescription',
          )}
          isPending={isPending}
          isDisabled={!scannerDetails?.isConnected}
          gridSize={oneBoxGridSize}
        />
        <ScannerConfigurationSwitch
          control={control}
          controlName="cgiScan"
          label={t('scannerConfiguration.cgiScanning')}
          description={t('scannerConfiguration.cgiScanningDescription')}
          isPending={isPending}
          isDisabled={!scannerDetails?.isConnected}
          gridSize={oneBoxGridSize}
        />
        <ScannerConfigurationSwitch
          control={control}
          controlName="bruteScan"
          label={t('scannerConfiguration.bruteForceScanning')}
          description={t('scannerConfiguration.bruteForceScanningDescription')}
          isPending={isPending}
          isDisabled={!scannerDetails?.isConnected}
          gridSize={oneBoxGridSize}
        />
        <ScannerConfigurationSwitch
          control={control}
          controlName="pingOnly"
          label={t('scannerConfiguration.pingOnlyDiscovery')}
          description={t('scannerConfiguration.pingOnlyDiscoveryDescription')}
          isPending={isPending}
          isDisabled={!scannerDetails?.isConnected}
          gridSize={oneBoxGridSize}
        />
      </Grid>
      <>
        {!hasScanOutsideWindowDisabledCapability && !isPending && (
          <Typography sx={{ mt: 4, mb: 2 }}>
            {t('scannerConfiguration.oldScanner')}
          </Typography>
        )}
        <Grid container spacing={4}>
          <ScannerConfigurationDropdown
            control={control}
            controlName="hostIdentificationScanIntensity"
            label={t('hostIdentificationScanIntensity.label')}
            description={t('hostIdentificationScanIntensity.description')}
            placeholder={t('scannerConfiguration.placeholder')}
            options={hostIdentificationScanningIntensityOptions}
            isPending={isPending}
            isDisabled={
              !hasScanOutsideWindowDisabledCapability ||
              !scannerDetails?.isConnected
            }
            gridSize={oneBoxGridSize}
          />
          <ScannerConfigurationDropdown
            control={control}
            controlName="hostIdentificationScanPeriod"
            label={t('hostIdentificationScanPeriod.label')}
            description={t('hostIdentificationScanPeriod.description')}
            placeholder={t('scannerConfiguration.placeholder')}
            options={scanPeriodOptions}
            isPending={isPending}
            isDisabled={
              !hasScanOutsideWindowDisabledCapability ||
              !scannerDetails?.isConnected
            }
            gridSize={oneBoxGridSize}
          />
          <ScannerConfigurationDropdown
            control={control}
            controlName="maxConcurrentVulnerabilityScans"
            label={t('maxConcurrentVulnerabilityScans.label')}
            description={t('maxConcurrentVulnerabilityScans.description')}
            placeholder={t('scannerConfiguration.placeholder')}
            options={maxConcurrentVulScansOptions}
            isPending={isPending}
            isDisabled={
              !hasScanOutsideWindowDisabledCapability ||
              !scannerDetails?.isConnected
            }
            gridSize={oneBoxGridSize}
          />
        </Grid>
      </>
    </Stack>
  );
};
